/**
 * Character validation functions
 */

import { RollResult } from './dice';
import type { Character } from '../types/character';

export interface ValidationResult {
  success: boolean;
  errors: string[];
  warnings?: string[];
}

export interface FieldValidationResult {
  isValid: boolean;
  error?: string;
  warning?: string;
}

export interface FormValidationState {
  [fieldName: string]: FieldValidationResult;
}

/**
 * Validate a character object
 */
export function validateCharacter(character: Character): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Basic required fields
  if (!character.id) {
    errors.push('Character ID is required');
  }

  if (!character.name || character.name.trim().length === 0) {
    errors.push('Character name is required');
  }

  if (!character.career || !character.career.id) {
    errors.push('Character career is required');
  }

  if (!character.homeworld || !character.homeworld.id) {
    errors.push('Character homeworld is required');
  }

  // Characteristics validation
  if (!character.characteristics) {
    errors.push('Character characteristics are required');
  } else {
    const requiredCharacteristics = [
      'weaponSkill',
      'ballisticSkill',
      'strength',
      'toughness',
      'agility',
      'intelligence',
      'perception',
      'willpower',
      'fellowship',
      'influence'
    ];

    for (const char of requiredCharacteristics) {
      const value = character.characteristics[char as keyof typeof character.characteristics];
      if (typeof value !== 'number' || value < 0 || value > 100) {
        errors.push(`Invalid ${char}: must be a number between 0 and 100`);
      }
    }
  }

  // Experience validation
  if (!character.experience) {
    errors.push('Character experience is required');
  } else {
    if (typeof character.experience.total !== 'number' || character.experience.total < 0) {
      errors.push('Total experience must be a non-negative number');
    }
    if (typeof character.experience.spent !== 'number' || character.experience.spent < 0) {
      errors.push('Spent experience must be a non-negative number');
    }
    if (typeof character.experience.available !== 'number' || character.experience.available < 0) {
      errors.push('Available experience must be a non-negative number');
    }

    // Check if experience adds up correctly
    if (
      character.experience.total !==
      character.experience.spent + character.experience.available
    ) {
      warnings.push('Experience totals do not add up correctly');
    }
  }

  // Derived stats validation
  if (!character.derivedStats) {
    errors.push('Character derived stats are required');
  } else {
    const derivedFields = [
      'wounds',
      'fatigue',
      'corruption',
      'insanity',
      'profitFactor',
      'initiative',
      'movementHalf',
      'movementFull',
      'movementCharge',
      'movementRun'
    ];
    for (const field of derivedFields) {
      const value = character.derivedStats[field as keyof typeof character.derivedStats];
      if (typeof value !== 'number' || value < 0) {
        errors.push(`${field} must be a non-negative number`);
      }
    }
  }

  // Array field validation
  const arrayFields = [
    'skills',
    'talents',
    'traits',
    'equipment',
    'weapons',
    'armor',
    'enabledRulebooks',
    'manualAdjustments'
  ];
  for (const field of arrayFields) {
    const value = character[field as keyof Character];
    if (!Array.isArray(value)) {
      errors.push(`${field} must be an array`);
    }
  }

  // Date validation
  if (!(character.createdAt instanceof Date) || isNaN(character.createdAt.getTime())) {
    errors.push('createdAt must be a valid date');
  }

  if (!(character.lastModified instanceof Date) || isNaN(character.lastModified.getTime())) {
    errors.push('lastModified must be a valid date');
  }

  // Schema version validation
  if (typeof character.schemaVersion !== 'number' || character.schemaVersion < 1) {
    errors.push('schemaVersion must be a positive number');
  }

  return {
    success: errors.length === 0,
    errors,
    warnings: warnings.length > 0 ? warnings : undefined
  };
}

/**
 * Validate a roll result object
 */
export function validateRollResult(rollResult: RollResult): ValidationResult {
  const errors: string[] = [];

  // Basic required fields
  if (typeof rollResult.raw !== 'number' || rollResult.raw < 1 || rollResult.raw > 100) {
    errors.push('Roll result must be a number between 1 and 100');
  }

  if (typeof rollResult.target !== 'number' || rollResult.target < 0) {
    errors.push('Target number must be a non-negative number');
  }

  if (!Array.isArray(rollResult.modifiers)) {
    errors.push('Modifiers must be an array');
  }

  if (typeof rollResult.total !== 'number') {
    errors.push('Total must be a number');
  }

  if (typeof rollResult.degrees !== 'number') {
    errors.push('Degrees must be a number');
  }

  if (typeof rollResult.success !== 'boolean') {
    errors.push('Success must be a boolean');
  }

  if (!Array.isArray(rollResult.tags)) {
    errors.push('Tags must be an array');
  }

  if (!(rollResult.timestamp instanceof Date) || isNaN(rollResult.timestamp.getTime())) {
    errors.push('Timestamp must be a valid date');
  }

  return {
    success: errors.length === 0,
    errors
  };
}

/**
 * Validate individual form fields
 */
export function validateCharacterName(name: string): FieldValidationResult {
  if (!name || name.trim().length === 0) {
    return { isValid: false, error: 'Character name is required' };
  }

  if (name.trim().length < 2) {
    return { isValid: false, error: 'Character name must be at least 2 characters long' };
  }

  if (name.length > 50) {
    return { isValid: false, error: 'Character name must be 50 characters or less' };
  }

  // Check for invalid characters
  const invalidChars = /[<>{}[\]\\]/;
  if (invalidChars.test(name)) {
    return { isValid: false, error: 'Character name contains invalid characters' };
  }

  return { isValid: true };
}

export function validateCharacteristic(value: number, name: string): FieldValidationResult {
  if (typeof value !== 'number' || isNaN(value)) {
    return { isValid: false, error: `${name} must be a valid number` };
  }

  if (value < 1 || value > 100) {
    return { isValid: false, error: `${name} must be between 1 and 100` };
  }

  if (value < 20) {
    return { isValid: true, warning: `${name} is very low (below 20)` };
  }

  if (value > 80) {
    return { isValid: true, warning: `${name} is very high (above 80)` };
  }

  return { isValid: true };
}

export function validateExperiencePoints(
  total: number,
  spent: number,
  available: number
): FieldValidationResult {
  if (typeof total !== 'number' || typeof spent !== 'number' || typeof available !== 'number') {
    return { isValid: false, error: 'Experience values must be numbers' };
  }

  if (total < 0 || spent < 0 || available < 0) {
    return { isValid: false, error: 'Experience values cannot be negative' };
  }

  if (total !== spent + available) {
    return { isValid: false, error: 'Experience totals do not add up correctly' };
  }

  if (spent > total) {
    return { isValid: false, error: 'Spent experience cannot exceed total experience' };
  }

  return { isValid: true };
}

export function validateNumericField(
  value: number,
  fieldName: string,
  min: number = 0,
  max?: number
): FieldValidationResult {
  if (typeof value !== 'number' || isNaN(value)) {
    return { isValid: false, error: `${fieldName} must be a valid number` };
  }

  if (value < min) {
    return { isValid: false, error: `${fieldName} must be at least ${min}` };
  }

  if (max !== undefined && value > max) {
    return { isValid: false, error: `${fieldName} must be at most ${max}` };
  }

  return { isValid: true };
}

/**
 * Validate data integrity and detect corruption
 */
export function validateDataIntegrity(data: unknown): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  try {
    // Check if data is an object
    if (typeof data !== 'object' || data === null) {
      errors.push('Data must be a valid object');
      return { success: false, errors };
    }

    // Check for circular references
    try {
      JSON.stringify(data);
    } catch {
      errors.push('Data contains circular references');
    }

    // Check for required top-level properties
    const requiredProps = ['id', 'name', 'schemaVersion'];
    for (const prop of requiredProps) {
      if (!(prop in data)) {
        errors.push(`Missing required property: ${prop}`);
      }
    }

    // Check for suspicious data patterns that might indicate corruption
    if ('characteristics' in data && data.characteristics) {
      const charValues = Object.values(data.characteristics);
      const allSame = charValues.every((val) => val === charValues[0]);
      if (allSame && charValues.length > 1) {
        warnings.push(
          'All characteristics have the same value - this might indicate data corruption'
        );
      }
    }

    // Check for null or undefined values in critical fields
    const criticalFields = ['id', 'name', 'career', 'homeworld'];
    for (const field of criticalFields) {
      if (data[field] === null || data[field] === undefined) {
        errors.push(`Critical field ${field} is null or undefined`);
      }
    }

    return {
      success: errors.length === 0,
      errors,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  } catch (error) {
    return {
      success: false,
      errors: [
        `Data integrity check failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      ]
    };
  }
}

/**
 * Validate that character data is self-contained.
 * Checks that skills and talents have all required fields for calculations
 * without external lookups.
 *
 * Requirements: 2.2, 2.5, 5.5, 5.6
 */
export function validateSelfContainedCharacter(character: Character): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate skills are self-contained
  if (Array.isArray(character.skills)) {
    for (const skill of character.skills) {
      // Check required fields for self-contained skill
      if (!skill.id || typeof skill.id !== 'string') {
        errors.push(`Skill missing string id: ${skill.name || 'unknown'}`);
      }

      if (!skill.name || typeof skill.name !== 'string') {
        errors.push(`Skill ${skill.id || 'unknown'} missing name`);
      }

      if (!skill.characteristic) {
        errors.push(`Skill ${skill.id || skill.name || 'unknown'} missing characteristic`);
      }

      // Check purchaseHistory is an array (required for self-contained data)
      if (!Array.isArray(skill.purchaseHistory)) {
        warnings.push(`Skill ${skill.id || skill.name || 'unknown'} missing purchaseHistory array`);
      } else {
        // Validate each purchase in history
        for (let i = 0; i < skill.purchaseHistory.length; i++) {
          const purchase = skill.purchaseHistory[i];
          if (!purchase.advancementId) {
            errors.push(`Skill ${skill.id || skill.name} purchase ${i} missing advancementId`);
          }
          if (typeof purchase.rank !== 'number') {
            warnings.push(`Skill ${skill.id || skill.name} purchase ${i} missing rank`);
          }
          if (typeof purchase.bonus !== 'number') {
            warnings.push(`Skill ${skill.id || skill.name} purchase ${i} missing bonus`);
          }
        }
      }
    }
  }

  // Validate talents are self-contained
  if (Array.isArray(character.talents)) {
    for (const talent of character.talents) {
      // Check required fields for self-contained talent
      if (!talent.id || typeof talent.id !== 'string') {
        errors.push(`Talent missing string id: ${talent.name || 'unknown'}`);
      }

      if (!talent.name || typeof talent.name !== 'string') {
        errors.push(`Talent ${talent.id || 'unknown'} missing name`);
      }

      // Check purchaseHistory is an array (required for self-contained data)
      if (!Array.isArray(talent.purchaseHistory)) {
        warnings.push(
          `Talent ${talent.id || talent.name || 'unknown'} missing purchaseHistory array`
        );
      } else {
        // Validate each purchase in history
        for (let i = 0; i < talent.purchaseHistory.length; i++) {
          const purchase = talent.purchaseHistory[i];
          if (!purchase.advancementId) {
            errors.push(`Talent ${talent.id || talent.name} purchase ${i} missing advancementId`);
          }
          if (typeof purchase.rank !== 'number') {
            warnings.push(`Talent ${talent.id || talent.name} purchase ${i} missing rank`);
          }
        }
      }

      // Check purchaseCount is set (required for self-contained data)
      if (typeof talent.purchaseCount !== 'number') {
        warnings.push(`Talent ${talent.id || talent.name || 'unknown'} missing purchaseCount`);
      }

      // Special validation for "Talented" talent
      if (talent.id === 'talented' || talent.id?.startsWith('talented-')) {
        // Check that affectedSkillId is set
        const hasAffectedSkillId =
          talent.affectedSkillId || talent.purchaseHistory?.some((p) => p.affectedSkillId);

        if (!hasAffectedSkillId && talent.purchaseHistory?.length > 0) {
          warnings.push(`Talented talent ${talent.id} missing affectedSkillId`);
        }
      }
    }
  }

  // Validate equipment uses string sourceId
  if (Array.isArray(character.equipment)) {
    for (const item of character.equipment) {
      if (!item.sourceId || typeof item.sourceId !== 'string') {
        warnings.push(`Equipment ${item.id || 'unknown'} missing string sourceId`);
      }
    }
  }

  return {
    success: errors.length === 0,
    errors,
    warnings: warnings.length > 0 ? warnings : undefined
  };
}
